import { formatDateFromISO, parseDateISO } from '@practium/shared';
import { MONTH_NAMES_PT } from '../constants/maintenance.constants';
import type { MaintenanceCyclePhase, MaintenanceCyclePhasesResponse } from '../types';

export type CyclePhaseRange = MaintenanceCyclePhase;

export interface CycleCalendarDay {
	day: number;
	phase: string | null;
}

export interface CycleMonthLegend {
	phase: string;
	firstDay: number;
	lastDay: number;
}

export interface CycleCalendarMonth {
	key: string;
	year: number;
	label: string;
	offset: number;
	days: CycleCalendarDay[];
	legend: CycleMonthLegend[];
}

export interface DynamicMonthlyParcel {
	key: string;
	phases: string[];
	parcel: Record<string, number>;
}

export const parseCycleDate = (value?: string | null) => {
	const date = parseDateISO((value ?? '').slice(0, 10));
	return date ? Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) : null;
};

const toIsoDate = (time: number) => new Date(time).toISOString().slice(0, 10);

const toMonthKey = (time: number) => toIsoDate(time).slice(0, 7);

const monthLegend = (days: CycleCalendarDay[]) =>
	days.reduce<CycleMonthLegend[]>((legend, { day, phase }) => {
		if (!phase) return legend;
		const last = legend[legend.length - 1];
		if (last?.phase === phase) {
			last.lastDay = day;
		} else {
			legend.push({ phase, firstDay: day, lastDay: day });
		}
		return legend;
	}, []);

export const buildCycleCalendarMonths = (phases: CyclePhaseRange[]): CycleCalendarMonth[] => {
	if (!phases.length) return [];
	const ranges = phases.map((range) => ({
		phase: range.phase,
		start: parseCycleDate(range.start) ?? 0,
		end: parseCycleDate(range.end) ?? 0,
	}));
	const first = new Date(ranges[0].start);
	const lastTime = ranges[ranges.length - 1].end;
	const months: CycleCalendarMonth[] = [];

	for (
		let cursor = Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), 1);
		cursor <= lastTime;
		cursor = Date.UTC(new Date(cursor).getUTCFullYear(), new Date(cursor).getUTCMonth() + 1, 1)
	) {
		const year = new Date(cursor).getUTCFullYear();
		const monthIndex = new Date(cursor).getUTCMonth();
		const daysInMonth = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
		const days = Array.from({ length: daysInMonth }, (_, index) => {
			const time = Date.UTC(year, monthIndex, index + 1);
			const range = ranges.find((item) => time >= item.start && time <= item.end);
			return { day: index + 1, phase: range?.phase ?? null };
		});
		months.push({
			key: toMonthKey(cursor),
			year,
			label: MONTH_NAMES_PT[String(monthIndex + 1).padStart(2, '0')] ?? '',
			offset: new Date(cursor).getUTCDay(),
			days,
			legend: monthLegend(days),
		});
	}

	return months;
};

export const getCyclePhasesByMonth = (phases: CyclePhaseRange[]) =>
	new Map(
		buildCycleCalendarMonths(phases).map((month) => [
			month.key,
			month.legend.map((item) => item.phase),
		])
	);

export const mapDynamicMonthlyParcels = ({
	phases,
	monthlyParcels,
}: MaintenanceCyclePhasesResponse): DynamicMonthlyParcel[] => {
	const phasesByMonth = getCyclePhasesByMonth(phases);
	return monthlyParcels.map(({ period, parcel }) => {
		const key = period.replace('/', '-');
		return { key, phases: phasesByMonth.get(key) ?? [], parcel };
	});
};

export interface CycleCalendarData {
	floweringDate: string;
	phases: CyclePhaseRange[];
	months: CycleCalendarMonth[];
}

const formatCycleDay = (isoDate: string) => formatDateFromISO(isoDate).slice(0, 5);

export const formatCyclePhaseRange = ({ start, end }: CyclePhaseRange) =>
	`${formatCycleDay(start)} – ${formatCycleDay(end)}`;

export const formatCycleDayRange = ({ firstDay, lastDay }: CycleMonthLegend) =>
	`${firstDay} a ${lastDay}`;

export const mapCycleCalendarSummary = ({ floweringDate, phases, months }: CycleCalendarData) => {
	const startYear = months[0]?.year;
	const endYear = months[months.length - 1]?.year;
	const season = startYear === endYear ? `${startYear}` : `${startYear}/${endYear}`;
	const floweringLabel = formatDateFromISO(floweringDate);
	const phaseCount = new Set(phases.map((range) => range.phase)).size;

	return {
		season,
		floweringLabel,
		subtitle: `Safra ${season} · florada em ${floweringLabel} · parcelamento semanal`,
		footer: `${months.length} ${months.length === 1 ? 'mês' : 'meses'} · ${phaseCount} ${phaseCount === 1 ? 'fase' : 'fases'}`,
	};
};
